import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import LeagueBadge from './LeagueBadge';

function WeeklyResultsModal({ recentHistory }) {
  const [show, setShow] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!recentHistory || recentHistory.length === 0) return;

    // Check the most recent completed week
    const lastWeek = recentHistory[0];
    if (!lastWeek || !lastWeek.status || lastWeek.status === 'new') return;

    // Only show once per week result
    const seenKey = `league-week-result-seen-${lastWeek.week}`;
    if (localStorage.getItem(seenKey)) return;

    setResult(lastWeek);
    setShow(true);
    localStorage.setItem(seenKey, 'true');
  }, [recentHistory]);

  if (!show || !result) return null;

  const isPromoted = result.status === 'promoted';
  const isRelegated = result.status === 'relegated';

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShow(false)}
        >
          <motion.div
            className="bg-puzzle-background rounded-xl p-6 max-w-sm mx-4 text-center"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-4xl mb-3">
              {/* eslint-disable-next-line no-nested-ternary */}
              {isPromoted ? '🎉' : isRelegated ? '😔' : '💪'}
            </div>

            <h3 className="text-xl font-bold text-puzzle-text mb-2">
              {isPromoted && 'Promoted!'}
              {isRelegated && 'Relegated'}
              {!isPromoted && !isRelegated && 'Cycle Complete'}
            </h3>

            <p className="text-puzzle-text-secondary mb-4">
              {isPromoted && 'Great work! You\'ve moved up a tier.'}
              {isRelegated && 'Keep pushing! You\'ve moved down a tier.'}
              {!isPromoted && !isRelegated && 'You held your position this cycle.'}
            </p>

            <div className="flex justify-center mb-4">
              <LeagueBadge tier={result.tier} size="lg" />
            </div>

            <p className="text-sm text-puzzle-text-secondary mb-4">
              Week of
              {' '}
              {result.week}
              {' '}
              &middot;
              {' '}
              {result.rating}
              {' '}
              pts
            </p>

            <button
              type="button"
              onClick={() => setShow(false)}
              className="px-6 py-2 bg-puzzle-blue text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

WeeklyResultsModal.propTypes = {
  recentHistory: PropTypes.arrayOf(PropTypes.shape({
    week: PropTypes.string,
    tier: PropTypes.string,
    status: PropTypes.string,
    rating: PropTypes.number,
  })),
};

WeeklyResultsModal.defaultProps = {
  recentHistory: [],
};

export default WeeklyResultsModal;
